import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export type ConfirmDialogAppearance = 'default' | 'modern';

@Component({
  selector: 'app-confirm-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div
      class="confirm-backdrop"
      *ngIf="open"
      [class.confirm-backdrop--modern]="appearance === 'modern'"
      (click)="onBackdropClick()"
      role="presentation"
    >
      <div
        class="confirm-panel"
        [class.confirm-panel--modern]="appearance === 'modern'"
        [class.confirm-panel--destructive]="destructive && appearance === 'modern'"
        role="dialog"
        aria-modal="true"
        [attr.aria-labelledby]="titleId"
        [attr.aria-describedby]="messageId"
        (click)="$event.stopPropagation()"
      >
        <ng-container *ngIf="appearance === 'modern'; else legacyPanel">
          <div class="confirm-modern__icon" *ngIf="destructive" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14Z"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
            </svg>
          </div>
          <h3 class="confirm-modern__title" [id]="titleId">{{ title }}</h3>
          <p class="confirm-modern__message" [id]="messageId">{{ message }}</p>
          <div class="confirm-modern__actions">
            <button
              type="button"
              class="confirm-modern__btn confirm-modern__btn--cancel"
              [disabled]="busy"
              (click)="cancel.emit()"
            >
              {{ cancelLabel }}
            </button>
            <button
              type="button"
              class="confirm-modern__btn"
              [class.confirm-modern__btn--danger]="destructive"
              [class.confirm-modern__btn--primary]="!destructive"
              [disabled]="busy"
              (click)="confirm.emit()"
            >
              {{ confirmLabel }}
            </button>
          </div>
        </ng-container>

        <ng-template #legacyPanel>
          <div class="page-card confirm-panel__legacy">
            <h3 class="panel-title" [id]="titleId">{{ title }}</h3>
            <p class="confirm-message" [class.confirm-message--danger]="destructive" [id]="messageId">
              {{ message }}
            </p>
            <div class="actions">
              <button type="button" class="btn ghost" [disabled]="busy" (click)="cancel.emit()">
                {{ cancelLabel }}
              </button>
              <button
                type="button"
                [class]="destructive ? 'btn warn' : 'btn primary'"
                [disabled]="busy"
                (click)="confirm.emit()"
              >
                {{ confirmLabel }}
              </button>
            </div>
          </div>
        </ng-template>
      </div>
    </div>
  `,
  styles: [
    `
      .confirm-backdrop {
        position: fixed;
        inset: 0;
        z-index: 1100;
        display: grid;
        place-items: center;
        background: rgba(15, 42, 74, 0.35);
        padding: 1rem;
      }

      .confirm-backdrop--modern {
        background: rgba(32, 33, 36, 0.42);
        backdrop-filter: blur(4px);
        -webkit-backdrop-filter: blur(4px);
        animation: confirm-backdrop-in 0.22s ease-out;
      }

      .confirm-panel {
        width: min(420px, 100%);
        margin: 0;
      }

      .confirm-panel--modern {
        width: min(400px, 100%);
        padding: 1.5rem 1.5rem 1.35rem;
        border: 1px solid #e8eaed;
        border-radius: 1rem;
        background: #fff;
        box-shadow:
          0 0 0 1px rgba(255, 255, 255, 0.8) inset,
          0 12px 40px rgba(60, 64, 67, 0.18),
          0 2px 8px rgba(60, 64, 67, 0.08);
        animation: confirm-panel-in 0.28s cubic-bezier(0.22, 1, 0.36, 1);
      }

      .confirm-panel__legacy {
        margin: 0;
      }

      .confirm-message {
        margin: 0.75rem 0 1rem;
        line-height: 1.5;
        color: #475569;
      }

      .confirm-message--danger {
        padding: 0.75rem;
        border: 1px solid #fecaca;
        border-radius: 6px;
        background: #fef2f2;
        color: #991b1b;
      }

      .confirm-modern__icon {
        display: grid;
        place-items: center;
        width: 3rem;
        height: 3rem;
        margin-bottom: 0.85rem;
        border-radius: 999px;
        background: #fce8e6;
        color: #c5221f;
      }

      .confirm-modern__title {
        margin: 0;
        font-family: 'Google Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        font-size: 1.25rem;
        font-weight: 600;
        letter-spacing: -0.02em;
        line-height: 1.35;
        color: #1f1f1f;
      }

      .confirm-modern__message {
        margin: 0.65rem 0 1.35rem;
        font-size: 0.9375rem;
        font-weight: 400;
        line-height: 1.55;
        color: #5f6368;
      }

      .confirm-panel--destructive .confirm-modern__message {
        margin-bottom: 1.5rem;
      }

      .confirm-modern__actions {
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-end;
        gap: 0.65rem;
      }

      .confirm-modern__btn {
        min-width: 5.5rem;
        padding: 0.55rem 1.1rem;
        border-radius: 999px;
        border: 1px solid transparent;
        font-family: inherit;
        font-size: 0.875rem;
        font-weight: 600;
        line-height: 1.25;
        cursor: pointer;
        transition:
          background 0.18s ease,
          border-color 0.18s ease,
          color 0.18s ease,
          box-shadow 0.18s ease;
      }

      .confirm-modern__btn:disabled {
        opacity: 0.55;
        cursor: not-allowed;
      }

      .confirm-modern__btn--cancel {
        border-color: #dadce0;
        background: #fff;
        color: #3c4043;
      }

      .confirm-modern__btn--cancel:hover:not(:disabled) {
        background: #f8f9fa;
        border-color: #c8ccd1;
      }

      .confirm-modern__btn--danger {
        background: #c5221f;
        color: #fff;
        box-shadow: 0 1px 2px rgba(197, 34, 31, 0.25);
      }

      .confirm-modern__btn--danger:hover:not(:disabled) {
        background: #a50e0e;
      }

      .confirm-modern__btn--primary {
        background: #0d9488;
        color: #fff;
      }

      .confirm-modern__btn--primary:hover:not(:disabled) {
        background: #0f766e;
      }

      @keyframes confirm-backdrop-in {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      @keyframes confirm-panel-in {
        from {
          opacity: 0;
          transform: translateY(10px) scale(0.98);
        }
        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
    `,
  ],
})
export class ConfirmDialogComponent {
  readonly titleId = 'confirm-dialog-title';
  readonly messageId = 'confirm-dialog-message';

  @Input() open = false;
  @Input() title = 'Confirm';
  @Input() message = 'Are you sure?';
  @Input() confirmLabel = 'Delete';
  @Input() cancelLabel = 'Cancel';
  @Input() destructive = true;
  @Input() appearance: ConfirmDialogAppearance = 'default';
  @Input() busy = false;
  @Input() closeOnBackdrop = true;

  @Output() confirm = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();

  onBackdropClick(): void {
    if (!this.closeOnBackdrop || this.busy) return;
    this.cancel.emit();
  }
}
